import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Button } from '@/components/buttons';
import { getExchangeInfo } from '@/components/dialogs/ExchangeCompoints/helpers';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  Units,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { Modals } from '@/helpers/modals';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import {
  LoyaltyDataI,
  MAX_PROGRESS,
} from '@/modules/loyalty/components/helpers';
import { useCommonStore, useModalsStore } from '@/store';

import { ScLoyaltySection } from './styled';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';

export interface LoyaltySectionProps {
  loyaltyData: LoyaltyDataI;
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const LoyaltySection: FC<LoyaltySectionProps> = ({
  loyaltyData,
  isMobile,
  onLinkClick,
}) => {
  const { t } = useTranslation(['loyalty', 'bonuses', 'common']);
  const { navigate } = useNavigation();
  const open = useModalsStore.use.open();
  const {
    images: {
      arrowDownGrayIcon: { component: ArrowDownGrayIcon },
    },
  } = useTheme();

  const compPoints = useCommonStore.use.compPoints();
  const compointsRatesMoney = useCommonStore.use.compointsRatesMoney();

  const { userCompoints, minExchange } = useMemo(
    () => getExchangeInfo(compointsRatesMoney, compPoints?.chargeable?.points),
    [compointsRatesMoney, compPoints?.chargeable?.points]
  );

  const [isFullLoyaltyInfoOpen, toggleFullLoyaltyInfo] = useToggle();

  const {
    level,
    nextPrizeLabel,
    nextPrizeValues,
    fromCompPoints,
    toCompPoints,
    currentCompPoints,
  } = loyaltyData;

  return (
    <ScLoyaltySection
      showFullInfo={isFullLoyaltyInfoOpen}
      onClick={() => isMobile && toggleFullLoyaltyInfo()}
    >
      <div className="info">
        <div className="level-info">
          <div className="text">
            <span>
              <b>{t('boostBlock.levelShort')}</b>
              {level}
            </span>
            <span>
              <b>{t('bonuses:exchangePoints.exchangeablePoints')}:</b>
              {compPoints?.chargeable?.points || 0}
            </span>
          </div>
          <div className="addons">
            <ImageComponent
              src="/loyalty/logo.png"
              width={80}
              height={44}
              alt="logo"
              objectFit="contain"
            />
            {isMobile && <ArrowDownGrayIcon />}
          </div>
        </div>
        {(!isMobile || isFullLoyaltyInfoOpen) && nextPrizeLabel && (
          <span>
            <b>{t('boostBlock.rewardGet')}</b>
            {t(nextPrizeLabel, nextPrizeValues)}
          </span>
        )}
      </div>

      <ProgressBar
        value={currentCompPoints}
        from={fromCompPoints}
        to={toCompPoints}
        mode={ProgressBarMode.PRIMARY_GRAY}
        showValue={ValuePosition.CENTER}
        height={24}
        borderRadius="24px"
        borderRadiusInner={
          currentCompPoints >= MAX_PROGRESS ? '24px' : '24px 0 0 24px'
        }
        units={Units.CP}
        withUnits
      />
      {(!isMobile || isFullLoyaltyInfoOpen) && (
        <div className="footer">
          <Button
            primary
            wide
            small
            onClick={() => open(Modals.ExchangeCompoints)}
            disabled={userCompoints < minExchange}
          >
            {t('bonuses:exchangePoints.exchange')} CP
          </Button>
          <Button
            grayTransparentBg
            wide
            small
            onClick={() => {
              onLinkClick && onLinkClick();
              navigate('/loyalty', true);
            }}
          >
            {t('common:menu.loyalty')}
          </Button>
        </div>
      )}
    </ScLoyaltySection>
  );
};
